import React, { Component } from 'react';
import path from 'path-browserify';
import classnames from 'classnames';
import debounce from 'lodash.debounce';
import { editor } from 'monaco-editor';

import Monaco, { suffixToLanguage } from '../../atoms/monaco';
import { IPromisesAPI } from 'memfs/lib/promises';

import styles from './editor.module.scss';

const EDITOR_BASE_OPTIONS: editor.IEditorOptions = {
	automaticLayout: false,
	scrollBeyondLastLine: false,
	lineNumbersMinChars: 2,
	lineDecorationsWidth: '6.8px',
	minimap: {
		enabled: false,
	},
};

const modelOptions = {
	tabSize: 2,
	insertSpaces: false,
};

type EditorProps = {
	fs?: IPromisesAPI;
	fontSize: number;
	filepath?: string;
	canWrite?: boolean;
};

type EditorState = {
	content?: string;
	isLoading: boolean;
};

export default class Editor extends Component<EditorProps> {
	static defaultProps = {
		fontSize: 13,
	};

	state: EditorState = {
		content: '',
		isLoading: false,
	};

	constructor(props: EditorProps) {
		super(props);
		this._loadFileFromFs();
	}

	componentWillReceiveProps(nextProps: EditorProps) {
		const prevProps = this.props;

		if (prevProps.filepath !== nextProps.filepath) {
			this.setState({ isLoading: true });
		}
	}

	componentDidUpdate(prevProps: EditorProps) {
		const nextProps = this.props;

		if (prevProps.fs !== nextProps.fs || prevProps.filepath !== nextProps.filepath) {
			this._writeFile.flush();
			this._loadFileFromFs();
		}
	}

	getOptions(): editor.IEditorOptions {
		const { fontSize, canWrite = true } = this.props;

		return {
			...EDITOR_BASE_OPTIONS,
			fontSize: fontSize,
			readOnly: !canWrite,
		};
	}

	getSuffix(currentFile?: string) {
		if (currentFile === undefined) return undefined;

		const suffix = currentFile
			.concat()
			.split('.')
			.pop();

		return suffix;
	}

	handleCodeChange = async (value: string, event?: any) => {
		const { fs, filepath } = this.props;
		const { content } = this.state;
		if (!fs || !filepath || content === undefined || value === content) return;

		this.setState({ content: value });

		this._writeFile(fs, filepath, value);
	};

	private __writeFile = async (fs: IPromisesAPI, filepath: string, value: string) => {
		await fs.mkdir(path.dirname(filepath), { recursive: true });
		return await fs.writeFile(filepath, value);
	};

	private _writeFile = debounce(this.__writeFile, 600);

	private _loadFileFromFs = async () => {
		const { fs, filepath } = this.props;
		if (!fs || !filepath) return;

		try {
			const content = await fs.readFile(filepath);
			if (typeof content !== 'string') {
				throw new TypeError(
					`non string file content: ${content && content.toString()}`
				);
			}

			this.setState({ content: content, isLoading: false });
		} catch (err) {
			this.setState({ content: undefined, isLoading: false });
			//TODO! reflect error to the user
			console.debug(`got error trying to read: "${filepath}"`, err);
		}
	};

	render() {
		const { filepath, fs } = this.props;
		const { content, isLoading } = this.state;

		const showEditor = fs && filepath && content !== undefined && !isLoading;

		const suffix = this.getSuffix(filepath) || 'js';
		const lang = suffixToLanguage.get(suffix) || suffixToLanguage.get('default');

		return (
			<div
				className={classnames(styles.editor, {
					[styles.showEditor]: showEditor,
				})}
			>
				<Monaco
					className={classnames(styles.monacoEditor, {
						[styles.hidden]: !showEditor,
					})}
					language={lang}
					theme="bit-vs-dark"
					value={content}
					editorOptions={this.getOptions()}
					textOptions={modelOptions}
					path={isLoading ? undefined : filepath} //@HACK! maybe add as disabled={} ?
					onChange={this.handleCodeChange}
				/>
			</div>
		);
	}
}
